import { useCallback, useState } from 'react';
import { sampleService, type Sample } from '../services/sample.service';
import { canManageSample } from '../utils/samplePermissions';
import { translateAxiosErrorMessage } from '../utils/error.utils';
import { logger, toLoggableError } from '../utils/logger';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type LoadEditableSampleArgs = {
  sampleId: number;
  userId: number | undefined;
  isAdmin: boolean;
  adminViewAll: boolean;
  t: TranslateFn;
  /** 一覧行など既知の Sample があるとき、先に権限チェックして無駄な getById を避ける */
  knownSample?: Sample;
  logContext?: string;
};

export type LoadEditableSampleResult =
  | { ok: true; sample: Sample }
  | { ok: false; error: string };

/**
 * 編集モーダル用にサンプル詳細を取得（権限チェック込み）。
 */
export async function loadEditableSample(
  args: LoadEditableSampleArgs
): Promise<LoadEditableSampleResult> {
  const {
    sampleId,
    userId,
    isAdmin,
    adminViewAll,
    t,
    knownSample,
    logContext = 'sample edit',
  } = args;

  if (
    knownSample &&
    !canManageSample(knownSample, userId, { isAdmin, adminViewAll })
  ) {
    return { ok: false, error: t('sample.case_manager.read_only_other_user') };
  }

  try {
    const response = await sampleService.getById(sampleId, {
      adminViewAll: adminViewAll || undefined,
    });
    const sample = response.data;
    if (!canManageSample(sample, userId, { isAdmin, adminViewAll })) {
      return { ok: false, error: t('sample.case_manager.read_only_other_user') };
    }
    return { ok: true, sample };
  } catch (err) {
    logger.error(`Failed to load sample for ${logContext}`, toLoggableError(err));
    return {
      ok: false,
      error: translateAxiosErrorMessage(err, t, t('sample.load_failed')),
    };
  }
}

type UseSampleEditLoaderArgs = {
  userId: number | undefined;
  isAdmin: boolean;
  adminViewAll: boolean;
  t: TranslateFn;
  onError: (message: string) => void;
  logContext?: string;
};

/**
 * 編集モーダル専用の選択状態（UsageRecords など selectedSample を他用途と共有しない画面向け）。
 */
export function useSampleEditLoader({
  userId,
  isAdmin,
  adminViewAll,
  t,
  onError,
  logContext = 'sample edit',
}: UseSampleEditLoaderArgs) {
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const closeEdit = useCallback(() => {
    setShowEditModal(false);
    setSelectedSample(null);
  }, []);

  const openEditById = useCallback(
    async (sampleId: number) => {
      onError('');
      const result = await loadEditableSample({
        sampleId,
        userId,
        isAdmin,
        adminViewAll,
        t,
        logContext,
      });
      if (result.ok === false) {
        onError(result.error);
        return;
      }
      setSelectedSample(result.sample);
      setShowEditModal(true);
    },
    [adminViewAll, isAdmin, logContext, onError, t, userId]
  );

  const openEdit = useCallback(
    async (sample: Sample) => {
      onError('');
      const result = await loadEditableSample({
        sampleId: sample.id,
        userId,
        isAdmin,
        adminViewAll,
        t,
        knownSample: sample,
        logContext,
      });
      if (result.ok === false) {
        onError(result.error);
        return;
      }
      setSelectedSample(result.sample);
      setShowEditModal(true);
    },
    [adminViewAll, isAdmin, logContext, onError, t, userId]
  );

  return {
    selectedSample,
    showEditModal,
    openEdit,
    openEditById,
    closeEdit,
  };
}
